import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Palette, Upload, Eye, RotateCcw, Check, Smartphone, Monitor, MessageSquare, ImageIcon, QrCode, ShieldCheck, ChevronDown, Plus, Trash2, Users, GripVertical, Type, ScrollText } from 'lucide-react';
import { adminApi } from '../../services/api';
import { apiUpload, apiError } from '../../services/publicApi';
import { PageTooltip } from '../components/TooltipSystem';
import { useBranding } from '../../context/BrandingContext';
import EventTermsTab from '../components/EventTermsTab';

import { getBackendUrl } from '../../services/origin';
const PRESET_PALETTES = [
  { name: 'Arancio (default)', primary: '#D44E20', secondary: '#3ECF8E', bg: '#111114', text: '#FFFFFF', textSec: '#888888' },
  { name: 'Blu Notte', primary: '#3B82F6', secondary: '#F59E0B', bg: '#0F172A', text: '#E2E8F0', textSec: '#94A3B8' },
  { name: 'Verde Bosco', primary: '#059669', secondary: '#F97316', bg: '#0C1A12', text: '#D1FAE5', textSec: '#6EE7B7' },
  { name: 'Viola Lusso', primary: '#8B5CF6', secondary: '#EC4899', bg: '#1A0B2E', text: '#E9D5FF', textSec: '#A78BFA' },
  { name: 'Rosso Vino', primary: '#DC2626', secondary: '#D97706', bg: '#1C0A0A', text: '#FEE2E2', textSec: '#FCA5A5' },
  { name: 'Teal Fresco', primary: '#0D9488', secondary: '#F472B6', bg: '#0A1A1A', text: '#CCFBF1', textSec: '#5EEAD4' },
];

const API = getBackendUrl();

const TABS = [
  { key: 'staff', label: 'Staff', icon: Smartphone },
  { key: 'customer', label: 'Bacheca', icon: Monitor },
  { key: 'register', label: 'Registrazione', icon: QrCode },
  { key: 'consent', label: 'Consenso', icon: ShieldCheck },
  { key: 'legal', label: 'Legale', icon: ScrollText },
];

export default function AdminBranding() {
  const { updateBranding } = useBranding();
  const [form, setForm] = useState({
    logo_url: '',
    primary_color: '#D44E20',
    secondary_color: '#3ECF8E',
    background_color: '#111114',
    text_color: '#FFFFFF',
    text_secondary_color: '#888888',
    extra_colors: [],
    fonts: {
      title: { source: 'system', name: 'DM Sans', url: '', weight: '700' },
      subtitle: { source: 'system', name: 'DM Sans', url: '', weight: '500' },
      body: { source: 'system', name: 'DM Sans', url: '', weight: '400' },
    },
    welcome_text: '',
    promo_banner_image: '',
    promo_banner_title: '',
    promo_banner_description: '',
    promo_banner_active: false,
    registration_hero_text: '',
    registration_subtitle: '',
  });
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(null);  // role being uploaded
  const [activeTab, setActiveTab] = useState('staff');
  const fileRef = useRef(null);
  const bannerRef = useRef(null);
  const fontFileRef = useRef({ title: null, subtitle: null, body: null });

  useEffect(() => {
    adminApi.get('/business/branding').then(brandRes => {
      const r = brandRes.data;
      const incomingFonts = r.fonts || {};
      setForm({
        logo_url: r.logo_url || '',
        primary_color: r.primary_color || '#D44E20',
        secondary_color: r.secondary_color || '#3ECF8E',
        background_color: r.background_color || '#111114',
        text_color: r.text_color || '#FFFFFF',
        text_secondary_color: r.text_secondary_color || '#888888',
        extra_colors: Array.isArray(r.extra_colors) ? r.extra_colors.slice(0, 10) : [],
        fonts: {
          title: { source: 'system', name: 'DM Sans', url: '', weight: '700', ...(incomingFonts.title || {}) },
          subtitle: { source: 'system', name: 'DM Sans', url: '', weight: '500', ...(incomingFonts.subtitle || {}) },
          body: { source: 'system', name: 'DM Sans', url: '', weight: '400', ...(incomingFonts.body || {}) },
        },
        welcome_text: r.welcome_text || '',
        promo_banner_image: r.promo_banner_image || '',
        promo_banner_title: r.promo_banner_title || '',
        promo_banner_description: r.promo_banner_description || '',
        promo_banner_active: r.promo_banner_active || false,
        registration_hero_text: r.registration_hero_text || '',
        registration_subtitle: r.registration_subtitle || '',
      });
      setBusinessName(r.business_name || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.put('/business/branding', {
        logo_url: form.logo_url || null,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        background_color: form.background_color,
        text_color: form.text_color,
        text_secondary_color: form.text_secondary_color,
        extra_colors: form.extra_colors || [],
        fonts: form.fonts,
        welcome_text: form.welcome_text || null,
        promo_banner_image: form.promo_banner_image || null,
        promo_banner_title: form.promo_banner_title || null,
        promo_banner_description: form.promo_banner_description || null,
        promo_banner_active: form.promo_banner_active,
        registration_hero_text: form.registration_hero_text || null,
        registration_subtitle: form.registration_subtitle || null,
      });
      updateBranding(res.data);
      toast.success('Branding aggiornato!');
    } catch {
      toast.error('Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const res = await apiUpload('/api/upload', fd, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status >= 200 && res.status < 300 && res.data?.url) {
        setForm(f => ({ ...f, logo_url: res.data.url }));
        toast.success('Logo caricato!');
      } else {
        toast.error(apiError(res));
      }
    } catch {
      toast.error('Errore upload logo');
    } finally { setUploading(false); }
  };

  const applyPreset = (p) => {
    setForm(f => ({ ...f, primary_color: p.primary, secondary_color: p.secondary, background_color: p.bg, text_color: p.text, text_secondary_color: p.textSec }));
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5MB)'); return; }
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const res = await apiUpload('/api/upload', fd, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status >= 200 && res.status < 300 && res.data?.url) {
        setForm(f => ({ ...f, promo_banner_image: res.data.url }));
        toast.success('Immagine banner caricata!');
      } else {
        toast.error(apiError(res));
      }
    } catch {
      toast.error('Errore upload banner');
    } finally { setUploadingBanner(false); }
  };

  const handleReset = () => {
    setForm({
      logo_url: '', primary_color: '#D44E20', secondary_color: '#3ECF8E', background_color: '#111114',
      text_color: '#FFFFFF', text_secondary_color: '#888888',
      welcome_text: '', promo_banner_image: '', promo_banner_title: '', promo_banner_description: '', promo_banner_active: false,
      registration_hero_text: '', registration_subtitle: '',
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-6xl" data-testid="admin-branding-page">
      <PageTooltip page="branding" />
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Branding</h1>
        <p className="text-[#64748B] text-sm">Personalizza l'aspetto di tutte le interfacce del tuo programma</p>
      </div>

      {/* Top tab switcher (always visible). Mirrors the live-preview tabs
          on the right column for the visual tabs, but lets the user reach
          the "Legale" view that has its own full-width layout. */}
      <div className="mb-5 flex bg-slate-100 rounded-lg p-0.5 w-fit" data-testid="branding-top-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              data-testid={`branding-tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === tab.key ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B]'}`}>
              <Icon size={13} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Legal tab uses its own layout (no phone preview, no color editor) */}
      {activeTab === 'legal' && <EventTermsTab />}

      {activeTab !== 'legal' && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* Logo */}
          <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm" data-testid="branding-logo-section">
            <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><Upload size={16} /> Logo</h3>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                ) : (
                  <span className="text-gray-300 text-3xl font-bold">{businessName.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn"
                  className="w-full h-9 bg-slate-100 hover:bg-gray-200 text-[#1A1A1A] text-xs font-medium rounded-lg transition-colors disabled:opacity-50">
                  {uploading ? 'Caricamento...' : 'Carica logo'}
                </button>
                {form.logo_url && (
                  <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))} data-testid="remove-logo-btn"
                    className="w-full h-9 text-[#EF4444] text-xs font-medium rounded-lg hover:bg-red-50 transition-colors">
                    Rimuovi logo
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                <p className="text-[#64748B] text-[10px]">PNG trasparente consigliato, max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm" data-testid="branding-colors-section">
            <h3 className="font-semibold text-[#1A1A1A] text-sm mb-4 flex items-center gap-2"><Palette size={16} /> Colori (globali)</h3>
            <div className="space-y-4">
              {[
                { key: 'primary_color', label: 'Primario', desc: 'Pulsanti, link, accent' },
                { key: 'secondary_color', label: 'Secondario', desc: 'Successo, highlights' },
                { key: 'background_color', label: 'Sfondo', desc: 'Sfondo pagine' },
                { key: 'text_color', label: 'Testo principale', desc: 'Titoli e testi primari' },
                { key: 'text_secondary_color', label: 'Testo secondario', desc: 'Sottotitoli, descrizioni' },
              ].map(c => (
                <div key={c.key} className="flex items-center gap-3">
                  <label className="relative cursor-pointer group">
                    <input type="color" value={form[c.key]} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                      data-testid={`color-${c.key}`} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    <div className="w-10 h-10 rounded-xl border-2 border-slate-200 group-hover:border-gray-400 transition-colors shadow-inner"
                      style={{ backgroundColor: form[c.key] }} />
                  </label>
                  <div className="flex-1">
                    <p className="text-[#1A1A1A] text-sm font-medium">{c.label}</p>
                    <p className="text-[#64748B] text-xs">{c.desc}</p>
                  </div>
                  <input type="text" value={form[c.key]} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                    className="w-24 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs font-mono text-[#1A1A1A] text-center focus:border-[#D44E20] focus:outline-none" />
                </div>
              ))}
            </div>

            {/* Extra accent palette (max 10 entries → palette grows to 12 with primary+secondary) */}
            <div className="mt-5 pt-4 border-t border-slate-100" data-testid="branding-extra-colors">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-[#1A1A1A]">Colori accent extra <span className="text-[#94A3B8] font-normal">({(form.extra_colors || []).length}/10 — palette {2 + (form.extra_colors || []).length}/12)</span></p>
                {(form.extra_colors || []).length < 10 && (
                  <button data-testid="add-extra-color"
                    onClick={() => setForm(f => ({ ...f, extra_colors: [...(f.extra_colors || []), { value: '#FFAA00', label: '' }] }))}
                    className="flex items-center gap-1 text-[11px] font-medium text-[#D44E20] hover:underline">
                    <Plus size={12} /> Aggiungi colore
                  </button>
                )}
              </div>
              {(form.extra_colors || []).length === 0 ? (
                <p className="text-[10px] text-[#94A3B8]">Aggiungi fino a 10 colori per banner, badge tier o sezioni dedicate. Saranno disponibili in tutti i selettori colore dell'app.</p>
              ) : (
                <div className="space-y-2">
                  {(form.extra_colors || []).map((c, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`extra-color-row-${idx}`}>
                      <label className="relative cursor-pointer group">
                        <input type="color" value={c.value || '#FFAA00'}
                          onChange={e => setForm(f => ({ ...f, extra_colors: f.extra_colors.map((x, i) => i === idx ? { ...x, value: e.target.value } : x) }))}
                          data-testid={`extra-color-${idx}`}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                        <div className="w-9 h-9 rounded-lg border-2 border-slate-200 group-hover:border-gray-400 transition-colors"
                          style={{ backgroundColor: c.value || '#FFAA00' }} />
                      </label>
                      <input type="text" value={c.label || ''} placeholder={`Etichetta (es. Accent ${idx + 1})`}
                        onChange={e => setForm(f => ({ ...f, extra_colors: f.extra_colors.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }))}
                        data-testid={`extra-color-label-${idx}`}
                        className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                      <input type="text" value={c.value || ''}
                        onChange={e => setForm(f => ({ ...f, extra_colors: f.extra_colors.map((x, i) => i === idx ? { ...x, value: e.target.value } : x) }))}
                        className="w-24 h-9 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs font-mono text-[#1A1A1A] text-center focus:border-[#D44E20] focus:outline-none" />
                      <button data-testid={`extra-color-remove-${idx}`}
                        onClick={() => setForm(f => ({ ...f, extra_colors: f.extra_colors.filter((_, i) => i !== idx) }))}
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-[#94A3B8] hover:bg-red-50 hover:text-[#EF4444] transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Presets */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-medium text-[#64748B] mb-2">Preset rapidi</p>
              <div className="grid grid-cols-2 gap-2">
                {PRESET_PALETTES.map(p => (
                  <button key={p.name} onClick={() => applyPreset(p)} data-testid={`preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 hover:border-gray-300 transition-all text-left">
                    <div className="flex gap-0.5 flex-shrink-0">
                      <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: p.primary }} />
                      <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: p.secondary }} />
                      <div className="w-3.5 h-3.5 rounded-full border border-slate-200" style={{ backgroundColor: p.bg }} />
                    </div>
                    <span className="text-[10px] font-medium text-[#64748B] truncate">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Custom Fonts */}
          <FontsSection form={form} setForm={setForm} fontFileRef={fontFileRef}
            uploadingFont={uploadingFont} setUploadingFont={setUploadingFont} api={API} />

          {/* Tab-specific settings */}
          {activeTab === 'customer' && <BachecaTab businessName={businessName} />}

          {activeTab === 'register' && (
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm" data-testid="branding-register-section">
              <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><QrCode size={16} /> Testi pagina registrazione</h3>
              <p className="text-[#64748B] text-xs mb-3">Personalizza i testi che i clienti vedono nella pagina di registrazione</p>
              <div className="space-y-3">
                <div>
                  <label className="text-[#64748B] text-[10px] font-medium block mb-1">Titolo principale</label>
                  <input type="text" value={form.registration_hero_text}
                    onChange={e => setForm(f => ({ ...f, registration_hero_text: e.target.value }))}
                    data-testid="registration-hero-input"
                    placeholder={`Es. Unisciti al programma fedeltà di ${businessName}!`}
                    maxLength={120}
                    className="w-full h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  <p className="text-[#64748B] text-[10px] mt-1">{form.registration_hero_text.length}/120</p>
                </div>
                <div>
                  <label className="text-[#64748B] text-[10px] font-medium block mb-1">Sottotitolo</label>
                  <input type="text" value={form.registration_subtitle}
                    onChange={e => setForm(f => ({ ...f, registration_subtitle: e.target.value }))}
                    data-testid="registration-subtitle-input"
                    placeholder="Es. Registrati e inizia a raccogliere punti"
                    maxLength={150}
                    className="w-full h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  <p className="text-[#64748B] text-[10px] mt-1">{form.registration_subtitle.length}/150</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'consent' && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-blue-700 text-xs">La pagina di consenso utilizza i colori e il logo del tuo branding. Il testo della privacy policy è configurabile nella sezione <strong>Locale</strong>.</p>
            </div>
          )}

          {activeTab === 'legal' && <EventTermsTab />}

          {activeTab === 'staff' && (
            <div className="bg-gray-50 rounded-xl p-4 border border-slate-100">
              <p className="text-[#64748B] text-xs">L'app staff utilizza i colori e il logo globali del tuo branding. Non ci sono impostazioni aggiuntive.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={handleReset} data-testid="reset-branding-btn" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[#64748B] text-sm font-medium hover:bg-slate-50 transition-colors">
              <RotateCcw size={14} /> Reset
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="save-branding-btn"
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#D44E20] text-white text-sm font-bold hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
              <Check size={14} /> {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </div>
        </div>

        {/* Right: Live Preview */}
        <div className="lg:col-span-3" data-testid="branding-preview-section">
          <div className="sticky top-8">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={16} className="text-[#64748B]" />
              <span className="text-sm font-medium text-[#64748B]">Anteprima live</span>
              <div className="flex ml-auto bg-slate-100 rounded-lg p-0.5">
                {TABS.filter(t => t.key !== 'legal').map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`preview-${tab.key}-btn`}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === tab.key ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B]'}`}>
                      <Icon size={12} /> {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Phone frame */}
            <div className="mx-auto w-[320px] rounded-[32px] border-4 border-gray-800 bg-gray-800 p-2 shadow-2xl">
              <div className="rounded-[24px] overflow-hidden" style={{ backgroundColor: form.background_color, minHeight: 560 }}>
                {activeTab === 'staff' && <StaffPreview form={form} name={businessName} />}
                {activeTab === 'customer' && <CustomerPreview form={form} name={businessName} />}
                {activeTab === 'register' && <RegistrationPreview form={form} name={businessName} />}
                {activeTab === 'consent' && <ConsentPreview form={form} name={businessName} />}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

/* ── Staff Preview ─────── */
function StaffPreview({ form, name }) {
  const tc = form.text_color || '#FFFFFF';
  const ts = form.text_secondary_color || '#888888';
  return (
    <div className="p-5 flex flex-col items-center pt-10">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 overflow-hidden">
        {form.logo_url ? (
          <img src={form.logo_url} alt="" className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full rounded-2xl flex items-center justify-center" style={{ backgroundColor: form.primary_color }}>
            <span className="text-white font-bold text-2xl">{name.charAt(0)}</span>
          </div>
        )}
      </div>
      <p className="font-bold text-lg mb-0.5" style={{ color: tc }}>{name}</p>
      <p className="text-xs mb-6" style={{ color: ts }}>Inserisci il PIN</p>
      <div className="flex gap-3 mb-6">
        {[0,1,2].map(i => <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: form.primary_color }} />)}
        {[3,4,5].map(i => <div key={i} className="w-3 h-3 rounded-full border-2" style={{ borderColor: ts }} />)}
      </div>
      <button className="w-full h-11 rounded-xl text-white text-sm font-bold" style={{ backgroundColor: form.primary_color }}>Entra</button>
      <div className="w-full mt-6 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center px-3 gap-2">
        <div className="w-4 h-4 rounded bg-white/10" />
        <div className="flex-1 h-2 rounded bg-white/5" />
      </div>
      <div className="grid grid-cols-3 gap-2 w-full mt-4">
        {['12', '340', '5'].map((v, i) => (
          <div key={i} className="rounded-xl bg-white/5 p-3 text-center">
            <p className="font-bold text-sm" style={{ color: tc }}>{v}</p>
            <div className="h-1 w-8 mx-auto rounded bg-white/10 mt-1" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 w-full mt-4">
        <div className="rounded-xl border p-3 text-center" style={{ borderColor: `${form.primary_color}40`, backgroundColor: `${form.primary_color}10` }}>
          <div className="w-5 h-5 mx-auto rounded mb-1" style={{ backgroundColor: `${form.primary_color}30` }} />
          <p className="text-[10px] font-medium" style={{ color: form.primary_color }}>Scansiona</p>
        </div>
        <div className="rounded-xl border p-3 text-center" style={{ borderColor: `${form.secondary_color}40`, backgroundColor: `${form.secondary_color}10` }}>
          <div className="w-5 h-5 mx-auto rounded mb-1" style={{ backgroundColor: `${form.secondary_color}30` }} />
          <p className="text-[10px] font-medium" style={{ color: form.secondary_color }}>Nuovo</p>
        </div>
      </div>
    </div>
  );
}

/* ── Customer Preview ─────── */
function CustomerPreview({ form, name }) {
  const tc = form.text_color || '#FFFFFF';
  const ts = form.text_secondary_color || '#888888';
  return (
    <div className="p-5 pt-8">
      {form.logo_url && <img src={form.logo_url} alt="" className="w-10 h-10 rounded-xl object-contain mb-2" />}
      <p className="text-xs font-medium mb-0.5" style={{ color: ts }}>{name}</p>
      <p className="text-lg font-bold mb-1" style={{ color: tc }}>{form.welcome_text || 'Ciao Marco!'}</p>
      {form.promo_banner_active && (form.promo_banner_title || form.promo_banner_image) && (
        <div className="rounded-xl overflow-hidden border border-white/10 mb-4" style={{ backgroundColor: `${form.primary_color}15` }}>
          {form.promo_banner_image && <img src={form.promo_banner_image} alt="" className="w-full h-20 object-cover" />}
          <div className="p-3">
            {form.promo_banner_title && <p className="text-xs font-bold" style={{ color: tc }}>{form.promo_banner_title}</p>}
            {form.promo_banner_description && <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: ts }}>{form.promo_banner_description}</p>}
          </div>
        </div>
      )}
      <div className="rounded-2xl p-4 border border-white/10 bg-white/5 mb-4">
        <p className="text-xs" style={{ color: ts }}>I tuoi punti</p>
        <p className="text-2xl font-bold mt-1" style={{ color: tc }}>340</p>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: '68%', backgroundColor: form.primary_color }} />
          </div>
          <span className="text-[10px]" style={{ color: ts }}>68%</span>
        </div>
      </div>
      <p className="text-sm font-bold mb-2" style={{ color: tc }}>Premi disponibili</p>
      {['Caffe gratis', 'Cornetto'].map((r, i) => (
        <div key={i} className="rounded-xl p-3 border border-white/5 bg-white/5 mb-2 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs font-semibold" style={{ color: tc }}>{r}</p>
            <div className="h-1 rounded-full bg-white/10 mt-1 w-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: i === 0 ? '100%' : '45%', backgroundColor: i === 0 ? form.secondary_color : form.primary_color }} />
            </div>
          </div>
          {i === 0 && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-lg" style={{ backgroundColor: `${form.secondary_color}20`, color: form.secondary_color }}>
              Riscattabile
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Registration Preview ─────── */
function RegistrationPreview({ form, name }) {
  const tc = form.text_color || '#FFFFFF';
  const ts = form.text_secondary_color || '#888888';
  const heroText = form.registration_hero_text || `Unisciti al programma fedelta di ${name}!`;
  const subtitle = form.registration_subtitle || 'Registrati e inizia a raccogliere punti';
  return (
    <div className="p-5 pt-8">
      <div className="text-center mb-5">
        {form.logo_url && <img src={form.logo_url} alt="" className="w-12 h-12 object-contain mx-auto mb-2" />}
        <p className="text-sm font-bold mb-1" style={{ color: tc }}>{heroText}</p>
        <p className="text-[10px]" style={{ color: ts }}>{subtitle}</p>
      </div>
      <div className="space-y-3">
        {[
          { label: 'Nome e cognome', placeholder: 'Mario Rossi' },
          { label: 'Numero di telefono', placeholder: '+39 333 1234567' },
          { label: 'Data di nascita', placeholder: 'GG/MM/AAAA' },
          { label: 'Email (facoltativa)', placeholder: 'mario@email.it' },
        ].map((f, i) => (
          <div key={i}>
            <p className="text-[9px] mb-1" style={{ color: ts }}>{f.label}</p>
            <div className="h-9 rounded-lg bg-white/5 border border-white/10 flex items-center px-3">
              <span className="text-[10px]" style={{ color: `${ts}88` }}>{f.placeholder}</span>
            </div>
          </div>
        ))}
        <button className="w-full h-10 rounded-lg text-white text-xs font-bold mt-1" style={{ backgroundColor: form.primary_color }}>Registrati</button>
        <div className="flex items-start gap-2 mt-2">
          <div className="w-3.5 h-3.5 rounded border-2 flex-shrink-0 mt-0.5" style={{ borderColor: ts }} />
          <p className="text-[8px] leading-relaxed" style={{ color: ts }}>
            Ho letto e compreso l'<span style={{ color: form.primary_color }} className="underline">informativa privacy</span> e accetto il trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Consent Preview ─────── */
function ConsentPreview({ form, name }) {
  const tc = form.text_color || '#FFFFFF';
  const ts = form.text_secondary_color || '#888888';
  return (
    <div className="p-5 pt-8 flex flex-col items-center">
      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: `${form.primary_color}20`, border: `2px solid ${form.primary_color}` }}>
        <ShieldCheck size={22} style={{ color: form.primary_color }} />
      </div>
      <p className="font-bold text-base text-center mb-1" style={{ color: tc }}>{name}</p>
      <p className="text-[10px] text-center mb-4" style={{ color: ts }}>
        Ciao <span style={{ color: tc }} className="font-medium">Marco</span>, leggi e accetta l'informativa sulla privacy.
      </p>
      {/* Privacy box mockup */}
      <div className="w-full bg-[#18181E] border border-white/10 rounded-xl p-3 mb-3" style={{ minHeight: 200 }}>
        <p className="text-white text-[10px] font-semibold mb-1">Informativa sulla Privacy</p>
        <p className="text-[#64748B] text-[8px] leading-relaxed">Ai sensi del GDPR 2016/679</p>
        <div className="mt-2 space-y-1.5">
          {['1. Titolare del trattamento', '2. Dati personali raccolti', '3. Finalità del trattamento', '4. Base giuridica'].map((s, i) => (
            <div key={i}>
              <p className="text-white text-[8px] font-medium">{s}</p>
              <div className="h-1 w-full rounded bg-white/5 mt-0.5" />
              <div className="h-1 w-3/4 rounded bg-white/5 mt-0.5" />
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-2">
          <ChevronDown size={14} style={{ color: form.primary_color }} className="animate-bounce" />
        </div>
      </div>
      <p className="text-[8px] text-center mb-2" style={{ color: `${ts}88` }}>Scorri fino in fondo per poter accettare</p>
      <button className="w-full h-10 rounded-xl text-white text-xs font-bold flex items-center justify-center gap-1.5 opacity-40" style={{ backgroundColor: '#333' }}>
        <ShieldCheck size={14} /> Accetto e mi iscrivo
      </button>
      <p className="text-[7px] text-center mt-2" style={{ color: `${ts}88` }}>Cliccando accetti il trattamento dei dati ai sensi del GDPR</p>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   BACHECA TAB — Targeted dashboard messages
   ═══════════════════════════════════════════════════════════════════════ */

const TIER_OPTIONS = ['bronze', 'silver', 'gold', 'platinum'];
const VISIT_PRESETS = [
  { label: 'Nuovi (0-2)', min: 0, max: 2 },
  { label: 'Ricorrenti (3-10)', min: 3, max: 10 },
  { label: 'Fedeli (10+)', min: 10, max: null },
];
const CHANNEL_OPTIONS = [
  { value: 'passaparola', label: 'Passaparola' },
  { value: 'social', label: 'Social' },
  { value: 'volantino', label: 'Volantino' },
  { value: 'google', label: 'Google' },
  { value: 'evento', label: 'Evento' },
  { value: 'passaggio', label: 'Passaggio' },
];
const ACTIVITY_OPTIONS = [
  { value: 'attivo', label: 'Attivi (30gg)', color: '#10B981' },
  { value: 'a_rischio', label: 'A rischio (30-60gg)', color: '#F59E0B' },
  { value: 'inattivo', label: 'Inattivi (60gg+)', color: '#EF4444' },
];

function BachecaTab({ businessName }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    adminApi.get('/admin/dashboard-messages')
      .then(r => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMessage = async (type) => {
    try {
      const defaults = type === 'welcome'
        ? { type: 'welcome', title: `Benvenuto al ${businessName}!`, description: '', active: true, priority: messages.length, audience: {} }
        : { type: 'banner', title: '', description: '', active: true, priority: messages.length, audience: {} };
      const r = await adminApi.post('/admin/dashboard-messages', defaults);
      setMessages(prev => [...prev, r.data]);
      toast.success(type === 'welcome' ? 'Messaggio benvenuto aggiunto' : 'Banner aggiunto');
    } catch { toast.error('Errore creazione messaggio'); }
  };

  const updateMessage = async (id, updates) => {
    try {
      const msg = messages.find(m => m.id === id);
      const r = await adminApi.put(`/admin/dashboard-messages/${id}`, { ...msg, ...updates });
      setMessages(prev => prev.map(m => m.id === id ? r.data : m));
    } catch { toast.error('Errore salvataggio'); }
  };

  const deleteMessage = async (id) => {
    try {
      await adminApi.delete(`/admin/dashboard-messages/${id}`);
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success('Messaggio eliminato');
    } catch { toast.error('Errore eliminazione'); }
  };

  if (loading) return <div className="text-center py-8 text-[#64748B] text-sm">Caricamento...</div>;

  const welcomes = messages.filter(m => m.type === 'welcome');
  const banners = messages.filter(m => m.type === 'banner');

  return (
    <div className="space-y-4" data-testid="bacheca-messages-tab">
      {/* Welcome messages */}
      <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2"><MessageSquare size={14} /> Messaggi di benvenuto</h3>
          <button onClick={() => addMessage('welcome')} data-testid="add-welcome-btn"
            className="flex items-center gap-1 px-2.5 py-1 bg-[#D44E20]/10 text-[#D44E20] text-[10px] font-semibold rounded-lg hover:bg-[#D44E20]/20">
            <Plus size={10} /> Aggiungi
          </button>
        </div>
        {welcomes.length === 0 ? (
          <p className="text-[#aaa] text-xs text-center py-3">Nessun messaggio di benvenuto. Il sistema mostrera il default.</p>
        ) : (
          <div className="space-y-3">
            {welcomes.map(m => <MessageEditor key={m.id} msg={m} onUpdate={updateMessage} onDelete={deleteMessage} />)}
          </div>
        )}
      </div>

      {/* Banners */}
      <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2"><ImageIcon size={14} /> Banner promozionali</h3>
          <button onClick={() => addMessage('banner')} data-testid="add-banner-btn"
            className="flex items-center gap-1 px-2.5 py-1 bg-[#D44E20]/10 text-[#D44E20] text-[10px] font-semibold rounded-lg hover:bg-[#D44E20]/20">
            <Plus size={10} /> Aggiungi
          </button>
        </div>
        {banners.length === 0 ? (
          <p className="text-[#aaa] text-xs text-center py-3">Nessun banner. Clicca + per aggiungerne uno.</p>
        ) : (
          <div className="space-y-3">
            {banners.map(m => <MessageEditor key={m.id} msg={m} onUpdate={updateMessage} onDelete={deleteMessage} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageEditor({ msg, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [local, setLocal] = useState(msg);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (field, value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const updateAudience = (field, value) => {
    setLocal(prev => ({ ...prev, audience: { ...prev.audience, [field]: value } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    await onUpdate(msg.id, local);
    setDirty(false);
    setSaving(false);
    toast.success('Salvato');
  };

  const audience = local.audience || {};
  const hasFilters = (audience.tiers?.length > 0) || audience.visit_range || (audience.channels?.length > 0) || (audience.activity?.length > 0);
  const audienceLabel = hasFilters ? 'Pubblico specifico' : 'Tutti i clienti';

  return (
    <div className={`border rounded-xl overflow-hidden ${local.active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`} data-testid={`msg-editor-${msg.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={12} className="text-[#ccc] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#1A1A1A] truncate">{local.title || (msg.type === 'welcome' ? 'Benvenuto' : 'Banner senza titolo')}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${msg.type === 'welcome' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
              {msg.type === 'welcome' ? 'Benvenuto' : 'Banner'}
            </span>
            <span className="text-[9px] text-[#64748B] flex items-center gap-0.5">
              <Users size={8} /> {audienceLabel}
            </span>
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); update('active', !local.active); }} data-testid={`toggle-msg-${msg.id}`}
          className={`w-8 h-5 rounded-full transition-colors relative flex-shrink-0 ${local.active ? 'bg-[#3ECF8E]' : 'bg-gray-200'}`}>
          <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-transform ${local.active ? 'translate-x-4' : 'translate-x-1'}`} />
        </button>
        <ChevronDown size={12} className={`text-[#64748B] transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="p-3 space-y-3 border-t border-slate-100">
          {/* Title */}
          <div>
            <label className="text-[10px] text-[#64748B] font-medium block mb-1">{msg.type === 'welcome' ? 'Testo benvenuto' : 'Titolo banner'}</label>
            <input type="text" value={local.title} onChange={e => update('title', e.target.value)}
              data-testid={`msg-title-${msg.id}`} maxLength={100}
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>

          {/* Description (banners only) */}
          {msg.type === 'banner' && (
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Descrizione</label>
              <textarea value={local.description} onChange={e => update('description', e.target.value)}
                data-testid={`msg-desc-${msg.id}`} maxLength={200} rows={2}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none resize-none" />
            </div>
          )}

          {/* Audience targeting */}
          <div className="bg-gray-50 rounded-lg p-3" data-testid={`msg-audience-${msg.id}`}>
            <p className="text-[10px] font-bold text-[#64748B] mb-2 flex items-center gap-1"><Users size={10} /> TARGET PUBBLICO</p>

            {/* Tiers */}
            <div className="mb-2">
              <p className="text-[9px] text-[#aaa] mb-1">Tier (vuoto = tutti)</p>
              <div className="flex flex-wrap gap-1">
                {TIER_OPTIONS.map(t => {
                  const sel = (audience.tiers || []).includes(t);
                  return (
                    <button key={t} onClick={() => {
                      const curr = audience.tiers || [];
                      updateAudience('tiers', sel ? curr.filter(x => x !== t) : [...curr, t]);
                    }} data-testid={`tier-filter-${t}-${msg.id}`}
                      className={`px-2 py-1 rounded-md text-[9px] font-medium border transition-all ${sel ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:border-gray-400'}`}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Activity */}
            <div className="mb-2">
              <p className="text-[9px] text-[#aaa] mb-1">Stato attivita (vuoto = tutti)</p>
              <div className="flex flex-wrap gap-1">
                {ACTIVITY_OPTIONS.map(a => {
                  const sel = (audience.activity || []).includes(a.value);
                  return (
                    <button key={a.value} onClick={() => {
                      const curr = audience.activity || [];
                      updateAudience('activity', sel ? curr.filter(x => x !== a.value) : [...curr, a.value]);
                    }} data-testid={`activity-filter-${a.value}-${msg.id}`}
                      className={`px-2 py-1 rounded-md text-[9px] font-medium border transition-all ${sel ? 'text-white border-transparent' : 'bg-white text-[#64748B] border-slate-200 hover:border-gray-400'}`}
                      style={sel ? { backgroundColor: a.color } : {}}>
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Visit range */}
            <div className="mb-2">
              <p className="text-[9px] text-[#aaa] mb-1">Range visite (vuoto = tutti)</p>
              <div className="flex flex-wrap gap-1">
                {VISIT_PRESETS.map(v => {
                  const vr = audience.visit_range;
                  const sel = vr && vr.min === v.min && vr.max === v.max;
                  return (
                    <button key={v.label} onClick={() => updateAudience('visit_range', sel ? null : { min: v.min, max: v.max })}
                      data-testid={`visit-filter-${v.min}-${msg.id}`}
                      className={`px-2 py-1 rounded-md text-[9px] font-medium border transition-all ${sel ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-[#64748B] border-slate-200 hover:border-gray-400'}`}>
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Channels */}
            <div>
              <p className="text-[9px] text-[#aaa] mb-1">Canale acquisizione (vuoto = tutti)</p>
              <div className="flex flex-wrap gap-1">
                {CHANNEL_OPTIONS.map(c => {
                  const sel = (audience.channels || []).includes(c.value);
                  return (
                    <button key={c.value} onClick={() => {
                      const curr = audience.channels || [];
                      updateAudience('channels', sel ? curr.filter(x => x !== c.value) : [...curr, c.value]);
                    }} data-testid={`channel-filter-${c.value}-${msg.id}`}
                      className={`px-2 py-1 rounded-md text-[9px] font-medium border transition-all ${sel ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-[#64748B] border-slate-200 hover:border-gray-400'}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {dirty && (
              <button onClick={save} disabled={saving} data-testid={`save-msg-${msg.id}`}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#D44E20] text-white text-[10px] font-bold rounded-lg hover:bg-[#B83D15] disabled:opacity-50">
                <Check size={10} /> {saving ? 'Salvando...' : 'Salva'}
              </button>
            )}
            <button onClick={() => { if (window.confirm('Eliminare questo messaggio?')) onDelete(msg.id); }}
              data-testid={`delete-msg-${msg.id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-[#ccc] text-[10px] hover:text-red-500 transition-colors">
              <Trash2 size={10} /> Elimina
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CUSTOM FONTS SECTION ──────────────────────────────────────────────────
const POPULAR_GOOGLE_FONTS = [
  'DM Sans', 'Inter', 'Poppins', 'Roboto', 'Montserrat', 'Open Sans',
  'Lato', 'Playfair Display', 'Merriweather', 'Bebas Neue', 'Oswald',
  'Raleway', 'Quicksand', 'Nunito', 'Work Sans', 'Space Grotesk',
];

function FontsSection({ form, setForm, fontFileRef, uploadingFont, setUploadingFont, api }) {
  const updateFont = (role, patch) => setForm(f => ({
    ...f,
    fonts: { ...f.fonts, [role]: { ...f.fonts[role], ...patch } },
  }));

  const handleUpload = async (role, file) => {
    if (!file) return;
    setUploadingFont(role);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const r = await apiUpload('/api/upload-font', fd, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.status < 200 || r.status >= 300) {
        throw new Error(apiError(r));
      }
      updateFont(role, { source: 'upload', name: file.name.replace(/\.(ttf|otf|woff2?|woff)$/i, ''), url: r.data.url });
      toast.success('Font caricato');
    } catch (e) {
      toast.error(e.message || 'Errore upload font');
    } finally {
      setUploadingFont(null);
    }
  };

  const ROLES = [
    { key: 'title', label: 'Titoli', desc: 'H1, intestazioni grandi', defaultWeight: '700' },
    { key: 'subtitle', label: 'Sottotitoli', desc: 'H2/H3, sezioni', defaultWeight: '500' },
    { key: 'body', label: 'Testo corpo', desc: 'Paragrafi, bottoni', defaultWeight: '400' },
  ];

  return (
    <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm" data-testid="branding-fonts-section">
      <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1 flex items-center gap-2"><Type size={16} /> Tipografia</h3>
      <p className="text-[#64748B] text-xs mb-4">Scegli i font per titoli, sottotitoli e testo. Google Fonts caricato in automatico oppure carica un TTF/WOFF2 dal tuo dispositivo.</p>

      <div className="space-y-4">
        {ROLES.map(role => {
          const fnt = form.fonts?.[role.key] || { source: 'system', name: 'DM Sans', weight: role.defaultWeight };
          return (
            <div key={role.key} className="border border-slate-100 rounded-xl p-3 bg-slate-50/40" data-testid={`font-${role.key}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-[#1A1A1A] text-sm font-medium">{role.label}</p>
                  <p className="text-[#64748B] text-[11px]">{role.desc}</p>
                </div>
                <div className="flex gap-1">
                  {['system', 'google', 'upload'].map(src => (
                    <button key={src} onClick={() => updateFont(role.key, { source: src })}
                      data-testid={`font-${role.key}-source-${src}`}
                      className={`px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors ${fnt.source === src ? 'bg-[#D44E20] text-white' : 'bg-white text-[#64748B] border border-slate-200 hover:border-gray-400'}`}>
                      {src === 'system' ? 'Sistema' : src === 'google' ? 'Google' : 'Carica'}
                    </button>
                  ))}
                </div>
              </div>

              {fnt.source === 'google' && (
                <div className="flex gap-2">
                  <input type="text" list={`google-fonts-${role.key}`}
                    placeholder="Es. Poppins"
                    value={fnt.name || ''}
                    onChange={e => updateFont(role.key, { name: e.target.value, url: '' })}
                    data-testid={`font-${role.key}-google-name`}
                    className="flex-1 h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  <datalist id={`google-fonts-${role.key}`}>
                    {POPULAR_GOOGLE_FONTS.map(n => <option key={n} value={n} />)}
                  </datalist>
                  <select value={fnt.weight || '400'}
                    onChange={e => updateFont(role.key, { weight: e.target.value })}
                    className="h-9 bg-white border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                    {['300', '400', '500', '600', '700', '800', '900'].map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
              )}

              {fnt.source === 'upload' && (
                <div className="space-y-2">
                  <input type="file" accept=".ttf,.otf,.woff,.woff2"
                    ref={el => { fontFileRef.current[role.key] = el; }}
                    onChange={e => handleUpload(role.key, e.target.files?.[0])}
                    className="hidden" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => fontFileRef.current[role.key]?.click()}
                      disabled={uploadingFont === role.key}
                      data-testid={`font-${role.key}-upload-btn`}
                      className="flex-1 h-9 bg-white border border-dashed border-slate-300 hover:border-[#D44E20] text-xs text-[#64748B] rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                      <Upload size={12} /> {uploadingFont === role.key ? 'Caricamento…' : (fnt.url ? 'Sostituisci file' : 'Carica TTF/WOFF2 (max 2MB)')}
                    </button>
                    {fnt.url && (
                      <button onClick={() => updateFont(role.key, { source: 'system', name: 'DM Sans', url: '' })}
                        data-testid={`font-${role.key}-remove`}
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-[#94A3B8] hover:bg-red-50 hover:text-[#EF4444] transition-colors">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  {fnt.url && (
                    <p className="text-[10px] text-[#10B981] truncate">✓ {fnt.name}</p>
                  )}
                </div>
              )}

              {fnt.source === 'system' && (
                <div className="flex gap-2">
                  <select value={fnt.name || 'DM Sans'}
                    onChange={e => updateFont(role.key, { name: e.target.value })}
                    data-testid={`font-${role.key}-system`}
                    className="flex-1 h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                    {['DM Sans', 'Inter', 'system-ui', 'Arial', 'Helvetica', 'Georgia', 'Times New Roman'].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <select value={fnt.weight || '400'}
                    onChange={e => updateFont(role.key, { weight: e.target.value })}
                    className="h-9 bg-white border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                    {['300', '400', '500', '600', '700'].map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
              )}

              {/* Live preview line */}
              <div className="mt-2 px-3 py-2 bg-white rounded-lg border border-slate-100" style={{
                fontFamily: fnt.name ? `"${fnt.name}", sans-serif` : 'inherit',
                fontWeight: fnt.weight || 400,
              }}>
                <span className="text-sm text-[#1A1A1A]">Aa Bb Cc 123 — Anteprima {role.label.toLowerCase()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
